import { DateTime } from "luxon";
import { translateRequestSubjectData } from "swing-utils";

import { type FeedbackRating } from "~components/ModalSubFeedback/ModalSubFeedback";
import { type SubLastFeedback } from "~components/Organisms/AdminExperience/GiveFeedback/GiveFeedback";
import { type TimesTableRowType } from "~components/RequestForm/hooks";
import { formatTimeAmPm, translate } from "~utils";

import {
  CancelRequestModel,
  FeedbackModel,
  IntervalModel,
  PositionModel,
  SchoolModel,
  SubModel,
  type IntervalResponse,
  type PositionResponse,
  type SubResponse,
} from ".";
import { ActivityTypes, LogEvent, type ActivityBase } from "./LogEvent";

/***** Response Types *****/
export type RequestDetailsResponse = {
  "client/date": string;
  "client/time": string;
  "db/id"?: number;
  "sub-req/sub"?: SubResponse;
  "sub-req/pending-sub"?: SubResponse;
  "sub-req/school": School;
  "sub-req/time": Array<IntervalResponse>;
  activity?: ActivityBase[];
  feedback?: FeedbackModel[];
  "sub-req/id"?: string;
  "sub-req/status"?: string;
  "will-still-be-invoiced?": boolean;
  "sub-req/teacher"?: TeacherKind;
  "sub-req/classroom-user"?: ClassroomUser;
  "sub-req/grade"?: string;
  "sub-req/subject": string;
  "sub-req/credential-required?": boolean;
  "sub-req/contact-name": string;
  "sub-req/contact-number": string;
  "sub-req/contact-email": string;
  "sub-req/day-of-contact"?: { "admin/id": string };
  "sub-req/lesson-plan-provided?"?: boolean;
  "sub-req/invoice-memo"?: string;
  "sub-req/memo": string;
  "sub-req/multi-day-adjustments"?: string;
  "sub-req/position"?: PositionResponse;
  "sub-req/preferred-subs": string[];
  "sub-req/extra-edits"?: string;
  "sub-req.index/start": string;
  "sub-req.index/end": string;
  "sub-req/budget"?: Budget;
  "has-feedback"?: boolean;
  "sub-req.index/short-code"?: string;
  "sub-req/pool"?: SubResponse[];
  "sub-req/accept-mode"?: string;
  "sub-req/tags"?: string[];
  "sub-last-feedback"?: {
    rating: FeedbackRating;
    date: string;
  };
};

/***** Model Types *****/
export const RequestStatus = {
  Filled: "sub-req.status/filled",
  Canceled: "sub-req.status/canceled",
  Submitted: "sub-req.status/submitted",
  ConfirmationPending: "sub-req.status/sub-confirmation-pending",
  Completed: "sub-req.status/completed",
  Closed: "sub-req.status/closed",
} as const;

export type School = {
  "db/id"?: number;
  "school/id": string;
  "school/name": string;
  "school/time-zone"?: string;
  "school/cancel-policy": number;
  "school/address": {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
};

type ClassroomUser = {
  "admin/id": string;
};

export type Budget = {
  id: string;
  name: string;
  status: "ACTIVE" | "INACTIVE";
  ytdSpent?: number;
  limit?: number;
  hideFromAdmin?: boolean;
  subscriberIds: string[];
  subscriberType: "DISTRICT" | "SCHOOL";
  subscriber: { name: string; id: string };
};

export type TeacherKind = "Roving" | "Vacancy";

/**
 * Model | RequestModel
 */
export class RequestModel {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private request: any;
  private _sub: SubModel;
  private _pendingSub: SubModel;
  private _school: SchoolModel;
  private _activity: LogEvent[];
  private _cancellation: CancelRequestModel | undefined;
  private _intervals: IntervalModel[];
  private _pool: SubModel[];
  private _feedback: FeedbackModel[];
  private _acceptMode: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(request: any) {
    this.request = request;
    this._sub = new SubModel(request?.["sub-req/sub"]);
    this._pendingSub = new SubModel(request?.["sub-req/pending-sub"]);
    this._school = new SchoolModel(request?.["sub-req/school"]);
    this._activity =
      request?.["activity"]?.map((event: ActivityBase) => {
        return new LogEvent(event, this._school.tzString);
      }) || [];
    this._acceptMode = request?.["sub-req/accept-mode"];

    if (request?.cancellation) {
      this._cancellation = new CancelRequestModel(
        request?.cancellation?.reason,
        request?.cancellation?.explanation,
      );
    }
    this._intervals =
      request?.["sub-req/time"]?.map((time: IntervalResponse) => {
        return new IntervalModel(time);
      }) || [];
    this._pool =
      request?.["sub-req/pool"]?.map((sub: SubResponse) => {
        return new SubModel(sub);
      }) || [];
    this._feedback =
      request?.["feedback"]?.map((feedback: FeedbackModel) => {
        return new FeedbackModel(feedback);
      }) || [];
  }

  get id(): string {
    return this.request?.["sub-req/id"];
  }

  get status(): string {
    return this.request?.["sub-req/status"];
  }

  get sub(): SubModel {
    return this._sub;
  }

  get pendingSub(): SubModel {
    return this._pendingSub;
  }

  get school(): SchoolModel {
    return this._school;
  }

  get activity(): LogEvent[] {
    return this._activity;
  }

  get cancellation(): CancelRequestModel | undefined {
    return this._cancellation;
  }

  get willStillBeInvoiced(): boolean {
    return this.request?.["will-still-be-invoiced?"];
  }

  get clientDate() {
    return this.request?.["client/date"];
  }

  get clientTime() {
    return formatTimeAmPm(this.request?.["client/time"]);
  }

  get teacher(): TeacherKind | undefined {
    return this.request?.["sub-req/teacher"];
  }

  get teacherId(): string {
    return this.request?.["sub-req/classroom-user"]?.["admin/id"];
  }

  get grade() {
    return this.request?.["sub-req/grade"];
  }

  get subject() {
    return this.request?.["sub-req/subject"];
  }

  get isCredentialRequired() {
    return this.request?.["sub-req/credential-required?"];
  }

  get contactName() {
    return this.request?.["sub-req/contact-name"];
  }

  get contactPhone() {
    return this.request?.["sub-req/contact-number"];
  }

  get contactEmail() {
    return this.request?.["sub-req/contact-email"];
  }

  get dayOfContactAdminId() {
    return this.request?.["sub-req/day-of-contact"]?.["admin/id"];
  }

  // Defaulting to `false` since the `formState` defaults this field to false
  // which causes the response comparison to be inaccurate. This should be fine
  // since if this field is empty, false || undefined means the same thing.
  get isLessonPlanProvided(): boolean {
    return this.request?.["sub-req/lesson-plan-provided?"] ?? false;
  }

  get internalCode() {
    return this.request?.["sub-req/invoice-memo"];
  }

  get notes() {
    return this.request?.["sub-req/memo"];
  }

  get multiDayAdjustments(): string | undefined {
    return this.request?.["sub-req/multi-day-adjustments"];
  }

  get subLastFeedback(): SubLastFeedback | undefined {
    return this.request?.["sub-last-feedback"];
  }

  getIntervals(sorted?: boolean): Array<IntervalModel> {
    if (sorted) {
      return [...this._intervals].sort((a: IntervalModel, b: IntervalModel) =>
        a.start < b.start ? -1 : 1,
      );
    }
    return this._intervals;
  }

  get pool(): Array<SubModel> {
    return this._pool.sort((a, b) => b.poolSortScore - a.poolSortScore);
  }

  get poolCount() {
    return this._pool.length;
  }

  get gradesArray(): Array<string> {
    if (!this.request?.["sub-req/grade"]) {
      return [];
    } else return this.request?.["sub-req/grade"]?.split(", ");
  }

  get subjectsArray(): Array<string> {
    if (!this.request?.["sub-req/subject"]) {
      return [];
    } else return this.request?.["sub-req/subject"]?.split(", ");
  }
  // The startTime and endtime fields are currently only used for validating the Single Day editing flow
  // TODO discuss removal once we refactor to no longer use the current validation solution

  get startTime(): DateTime {
    const sdInterval = new IntervalModel(this.request?.["sub-req/time"][0]);
    return sdInterval.start;
  }

  get endTime(): DateTime {
    const sdInterval = new IntervalModel(this.request?.["sub-req/time"][0]);
    return sdInterval.end;
  }

  get preferredSubs() {
    return this.request?.["sub-req/preferred-subs"];
  }

  get position(): PositionModel {
    return new PositionModel(this.request?.["sub-req/position"]);
  }

  get additionalEdits() {
    return this.request?.["sub-req/extra-edits"];
  }

  get budget(): Budget | undefined {
    return this.request?.["sub-req/budget"];
  }

  get feedback(): FeedbackModel[] {
    return this._feedback;
  }

  get startDate() {
    return DateTime.fromISO(this.request?.["sub-req.index/start"]);
  }

  get endDate() {
    return DateTime.fromISO(this.request?.["sub-req.index/end"]);
  }

  get hasFeedback(): boolean | undefined {
    return this.request?.["has-feedback"];
  }

  get recentlyTrimmed(): boolean {
    return Boolean(this.request?.["recently-trimmed"]);
  }

  get waitlistAutoFill(): boolean {
    return Boolean(this.request?.["sub-req/waitlist-auto-fill?"]);
  }

  isMultiDayRequest(): boolean {
    return this.request?.["sub-req/time"]?.length > 1;
  }

  isFilled(): boolean {
    return this.status === RequestStatus.Filled;
  }

  isCanceled(): boolean {
    return this.status === RequestStatus.Canceled;
  }

  isConfirmationPending(): boolean {
    return this.status === RequestStatus.ConfirmationPending;
  }

  isCompleted(): boolean {
    return this.status === RequestStatus.Completed;
  }

  isClosed(): boolean {
    return this.status === RequestStatus.Closed;
  }

  isPast(): boolean {
    const end = DateTime.fromISO(this.request?.["sub-req.index/end"]);
    const now = DateTime.now();
    return end < now;
  }

  isLTRequestPending(): boolean {
    return !!this.multiDayAdjustments;
  }

  isWaitlistReq(): boolean {
    return this._acceptMode === "sub-req.accept-mode/admin-choice";
  }

  isStarted(): boolean {
    const timesSorted = this.getIntervals(true);
    const end = timesSorted[0].end;
    const now = DateTime.now();
    return now > end;
  }

  getCommonRequestFields() {
    const commonFields = {
      school: this.school.id,
      teacher: this.teacher,
      grade: this.gradesArray,
      subject: this.subjectsArray,
      contactPhone: this.contactPhone,
      dayOfContactAdminId: this.dayOfContactAdminId,
      internalCode: this.internalCode,
      isCredentialRequired: this.isCredentialRequired,
      isLessonPlanProvided: this.isLessonPlanProvided,
      memo: this.notes,
      budgetId: this.budget?.id,
      waitlistAutoFill: this.waitlistAutoFill,
    };

    return {
      ...commonFields,
      ...(this.position.id ? { position: this.position.id } : {}),
    };
  }

  getMultiDayRequestFields(cachedIntervals: Array<TimesTableRowType>): Record<string, unknown> {
    return {
      ...this.getCommonRequestFields(),
      intervals: cachedIntervals,
      multiDayAdjustments: this.multiDayAdjustments,
    };
  }

  getFilledRequestFields(cachedIntervals: Array<TimesTableRowType>) {
    return {
      intervals: cachedIntervals,
      contactPhone: this.contactPhone,
      dayOfContactAdminId: this.dayOfContactAdminId,
      internalCode: this.internalCode,
      memo: this.notes,
      additionalEdits: this.additionalEdits,
      budgetId: this.budget?.id,
      school: this.school.id,
      isLessonPlanProvided: this.isLessonPlanProvided,
      teacher: this.teacher,
      grade: this.gradesArray,
      subject: this.subjectsArray,
    };
  }

  getTeacherDisplay() {
    switch (this.teacher) {
      case "Roving":
        return translate("TEACHER_LABEL_ROVING");
      case "Vacancy":
        return translate("TEACHER_LABEL_VACANCY");
      default:
        return this.teacher;
    }
  }

  getGradeDisplay(): string {
    return this.grade
      ? this.grade === translate("UNKNOWN")
        ? translate("UNKNOWN_SEE_NOTES")
        : this.grade
      : translate("NOT_PROVIDED");
  }

  getSubjectDisplay(params: { showUnknownAsNote: boolean }): string {
    const { showUnknownAsNote } = params;

    if (!this.subject) {
      return translate("NOT_PROVIDED");
    }

    const translatedSubjects = translateRequestSubjectData({
      subjects: this.subject,
    });

    const finalSubjects = showUnknownAsNote
      ? translatedSubjects.map((subject) =>
          subject === translate("UNKNOWN") ? translate("UNKNOWN_SEE_NOTES") : subject,
        )
      : translatedSubjects;

    return finalSubjects.join(", ");
  }

  /**
   * Returns true if the sub completed work before the request was canceled.
   * This is used to determine if the sub should be reviewed.
   */
  didSubCompleteWorkBeforeCancel(): boolean {
    if (!this.isCanceled()) return false;

    const startTime = this.startDate;

    const cancelEvent = this.activity.find(
      (event) => event?.action === ActivityTypes.SUB_REQ_CANCELED,
    );

    if (!cancelEvent || !cancelEvent.timestamp) return false;

    // If the cancel event happened before the start time,
    // the sub did not complete work.
    return DateTime.fromISO(cancelEvent.timestamp) > startTime;
  }
}
